/**
 * useActiveVersionDrift — TanStack Query hook over
 * `GET /api/conversations/:id/active-version-sync` (Phase-2 endpoint, ADR 0001).
 *
 * builder-ux-overhaul Phase 4 Session 1 (2026-05-22).
 *
 * Returns `ActiveVersionSync` — `{hasVersion, inSync, currentVersionId,
 * currentVersionLabel}` — which feeds:
 *   - S2: the 3-state `<ActiveVersionPill>` (`◌` / `▸` / `▲`)
 *   - S4: the `<SendDealLinkButton>` share-gate additive predicate
 *
 * Key-nesting:
 *   `queryKeys.conversations.detail(id)` + `'active-version-sync'` suffix so
 *   every `conversations.all` invalidation (cmv ADR-0020 cascade, optimistic
 *   post-stream patch, Realtime echo) sweeps this query → pill recomputes
 *   fresh rather than from a stale closure (Phase-2 carry-forward #4 /
 *   § 11 risk #6).
 *
 * Error semantics (Phase-2 carry-forward #2):
 *   - Thrown `ApiError` with `status: 404` = ACCESS error (cross-user/unknown).
 *     Consumers (S2) must render nothing, not a no-version pill.
 *   - 200 body with `hasVersion: false` = real no-version state → `◌` pill.
 *   These two paths MUST NOT be conflated.
 *
 * Envelope shape: standard `sendSuccess` `{success, data}` envelope — NOT the
 * bare `/views` shape (ADR-0017 exception). Inline declaration per the
 * `use-conversation-versions.ts` option-(b) idiom; `api.ts` is not extended
 * for this single consumer.
 *
 * `inSync` is ALWAYS boolean, never null (Phase-2 carry-forward #3) — the gate
 * `!hasVersion || !inSync` and the 3-state mapping need no null-guard.
 */
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import type { ActiveVersionSync } from '@arcanum-hq/shared-types';

/** Standard `sendSuccess` envelope shape for the active-version-sync endpoint. */
interface ActiveVersionSyncEnvelope {
  success: boolean;
  data: ActiveVersionSync;
}

export function useActiveVersionDrift(
  conversationId: string | null | undefined
) {
  return useQuery<ActiveVersionSync, ApiError>({
    // Nested under conversations.detail(id) so `conversations.all` invalidation
    // (prefix-match — TanStack default) sweeps this suffixed key automatically.
    queryKey: [
      ...queryKeys.conversations.detail(conversationId ?? ''),
      'active-version-sync',
    ],
    queryFn: async (): Promise<ActiveVersionSync> => {
      // option (b) — declare the {success, data} envelope shape locally
      // (mirrors use-conversation-versions.ts line 115: api.get<...> then .data)
      const res = await api.get<ActiveVersionSyncEnvelope>(
        `/api/conversations/${conversationId}/active-version-sync`
      );
      return res.data;
    },
    enabled: !!conversationId,
  });
}
